import React, { Suspense } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';

// Globální import Bootstrap Icons CSS
import 'bootstrap-icons/font/bootstrap-icons.css';
// Styly
import '../../css/Ppd.css'
import '../../css/MobileVariables.css';
import '../../css/Mobile.css';

// Import komponent/ modulu, které budou vždy na stránce
import Favorites from './Components/Favorites/Favorites';
import LanguageSelector from './Components/LanguageSelector/LanguageSelector';

// Definice lazy komponent
const SearchInputModule = React.lazy(() => import('./Components/SearchInput/SearchInput'));
const SearchResultsGridModule = React.lazy(() => import('./Components/FileManager/PublishingTasks/SearchResultsGrid'));
const DataGridModule = React.lazy(() => import('@ogaf/react.scripts/Components/DataGrid/DataGrid'));
const FileManager = React.lazy(() => import('./Components/FileManager/FileManager'));
const FolderPickerTree = React.lazy(() => import('./Components/FileManager/FolderPickerTree'));

// Mapa komponent (lazy + normální)
const componentMap = {
    // klasika
    'Favorites': Favorites, 
    'LanguageSelector': LanguageSelector,
    // lazy
    'SearchInput': SearchInputModule,
    'SearchResultsGrid': SearchResultsGridModule,
    'DataGrid': DataGridModule,
    'FileManager': FileManager,
    'FolderPickerTree': FolderPickerTree,
    'FileManagerPublishingTasks': FileManager
} as const;

// Loading komponenta
const LoadingSpinner = () => (
    <div
        style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 9999
        }}
    >
        <div
            className="spinner-border"
            style={{ color: '#002b59' }}
        >
            <span className="visually-hidden">Načítám...</span>
        </div>
    </div>
);

// Error boundary komponenta
class ErrorBoundary extends React.Component<
    { children: React.ReactNode },
    { hasError: boolean; error?: Error }
> {
    /**
     * Konstuktor.
     * @param props
     */
    constructor(props: { children: React.ReactNode }) {
        super(props);
        this.state = { hasError: false };
    }

    /**
     * Metoda volaná při zachycení chyby v komponentě.
     * @param error Zachycená chyba.
     * @returns Nový stav komponenty.
     */
    static getDerivedStateFromError(error: Error) : { hasError: boolean; error: Error } {
        return { hasError: true, error };
    }

    /**
     * Metoda renderuje obsah. Po zachycení chyby, umožňuje logování.
     * @returns React element nebo react node.
     */
    render(): React.ReactNode {
        if (this.state.hasError) {
            return (
                <div className="alert alert-danger">
                    <h6>Chyba při načítání komponenty</h6>
                    <small>{this.state.error?.message}</small>
                </div>
            );
        }
        return this.props.children;
    }
}

// Cache vytvořených rootů podle ID kontejneru - zabraňuje opakovanému volání `createRoot`
// nad stejným DOM uzlem (typicky při opětovném zobrazení popupu), které by jinak
// skončilo varováním "createRoot() on a container that has already been passed to createRoot()".
const rootsByContainerId = new Map<string, ReturnType<typeof createRoot>>();

// Nová renderovací funkce
const renderReactComponent = (componentName: string, containerId: string, props: any = {}, componentRef?: React.Ref<any>, key?: string | number) => {
    const container = document.getElementById(containerId);
    if (!container) {
        console.error(`Container with id "${containerId}" not found!`);
        return;
    }

    const Component = componentMap[componentName as keyof typeof componentMap];
    if (!Component) {
        console.error(`Component "${componentName}" not found!`);
        return;
    }

    let root = rootsByContainerId.get(containerId);
    if (!root) {
        root = createRoot(container);
        rootsByContainerId.set(containerId, root);
    }

    // Wrappování s Suspense a ErrorBoundary
    root.render(
        <BrowserRouter>
            <ErrorBoundary>
                <Suspense fallback={<LoadingSpinner />}>
                    <Component key={key} {...props} ref={componentRef} />
                </Suspense>
            </ErrorBoundary>
        </BrowserRouter>
    );
};

(globalThis as any).renderReactComponent = renderReactComponent;

export default renderReactComponent;